import { Card, CardContent } from "@/components/ui/card";
import { useState, useEffect } from "react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  Calendar, MapPin, Route, ExternalLink,
  Gauge, Clock, Mountain, Navigation, Bed,
  Sun, CloudSun, Cloud, CloudRain, Snowflake, Globe, UtensilsCrossed, Star, ChevronDown, ChevronUp,
} from "lucide-react";
import { RouteMapViewer } from "@/components/RouteMapViewer";
import ElevationProfileWeb from "@/components/ElevationProfileWeb";
import ElevationHistogramWeb from "@/components/ElevationHistogramWeb";
import { getTranslatedValue } from "@/components/TranslatableField";
import { handleUnauthorizedAccess } from "@/lib/access-control";
import { getUploadUrl, getCloudinaryThumbnail } from "@/lib/utils";
import { BACKEND_URL } from "@/config";

const API = BACKEND_URL;

// Compact route display: first → last if >threshold, expandable middle
const COLLAPSE_THRESHOLD = 6;

function LocalitiesRoute({ localities, isAuthenticated, navigate, tripId, setHighlightedPoint, t }) {
  // b99.9.99 — Always render the full vertical list of localities (no
  // expand/collapse toggle). Renamed "ROTA" to "SEQUÊNCIA DE LOCALIDADES"
  // to better reflect that this list may contain repetitions (e.g. in
  // circular routes) — it's the literal sequence of points the rider
  // passes through, not a deduplicated set.
  const first = localities[0];
  const lastIdx = localities.length - 1;

  const renderLoc = (loc, idx, isEndpoint = false) => (
    <TooltipProvider key={`${loc.locality_id}-${idx}`}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex items-center gap-1 text-[11px] md:text-xs"
            onMouseEnter={() => loc.lat && loc.lng && setHighlightedPoint({ lat: loc.lat, lng: loc.lng })}
            onMouseLeave={() => setHighlightedPoint(null)}>
            {loc.lat && loc.lng ? (
              <a href={`https://www.google.com/maps/search/?api=1&query=${loc.lat},${loc.lng}`} target="_blank" rel="noopener noreferrer"
                className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md transition-colors ${isEndpoint ? "bg-primary/10 hover:bg-primary/20" : "bg-primary/5 hover:bg-primary/10"}`}
                onClick={(e) => { if (!isAuthenticated) { e.preventDefault(); handleUnauthorizedAccess(navigate, { returnTo: `/passeio/${tripId}`, message: t("trip_detail.auth_required.maps") }); window.scrollTo(0, 0); } }}>
                <Navigation className={`h-2.5 w-2.5 ${isEndpoint ? "text-primary" : "text-muted-foreground"}`} />
                <span className={isEndpoint ? "text-foreground font-medium" : "text-foreground"}><span className="md:hidden">{loc.name.length > 20 ? loc.name.slice(0, 20) + "..." : loc.name}</span><span className="hidden md:inline">{loc.name}</span></span>
              </a>
            ) : (
              <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md ${isEndpoint ? "bg-muted/80" : "bg-muted"}`}>
                <Navigation className={`h-2.5 w-2.5 ${isEndpoint ? "text-primary" : "text-muted-foreground"}`} />
                <span className={isEndpoint ? "font-medium" : ""}><span className="md:hidden">{loc.name.length > 20 ? loc.name.slice(0, 20) + "..." : loc.name}</span><span className="hidden md:inline">{loc.name}</span></span>
              </span>
            )}
          </span>
        </TooltipTrigger>
        <TooltipContent><p>{loc.lat && loc.lng ? t("trip_detail.locality_tooltip_with_gps") : t("trip_detail.locality_tooltip_no_gps")}</p></TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );

  return (
    <div>
      <div className="flex items-center gap-1.5 mb-2">
        <Route className="h-3 w-3 text-primary" />
        <span className="text-xs font-semibold text-foreground uppercase tracking-wider">{t("trip_detail.locality_sequence_label", "Localidades visitadas (percurso)")}</span>
      </div>
      <div className="flex flex-col gap-0.5">
        {localities.map((loc, idx) => (
          <div key={`vlist-${idx}`} className="flex items-center gap-1.5">
            <span className="text-muted-foreground text-[9px] w-4 text-right shrink-0">{idx + 1}.</span>
            {renderLoc(loc, idx, idx === 0 || idx === lastIdx)}
          </div>
        ))}
      </div>
    </div>
  );
}

// Collapsible POIs list: show all if <=threshold, else first+last with expand
function PoisList({ pois, isAuthenticated, navigate, tripId, setHighlightedPoint, t }) {
  const [expanded, setExpanded] = useState(false);
  const needsCollapse = pois.length > COLLAPSE_THRESHOLD;
  const visible = needsCollapse && !expanded ? [pois[0], pois[pois.length - 1]] : pois;

  const renderPoi = (poi, idx) => (
    <TooltipProvider key={`${poi.poi_id}-${idx}`}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex items-center gap-1 text-[11px] md:text-xs"
            onMouseEnter={() => poi.lat != null && poi.lng != null && setHighlightedPoint({ lat: poi.lat, lng: poi.lng })}
            onMouseLeave={() => setHighlightedPoint(null)}>
            {poi.lat != null && poi.lng != null ? (
              <a href={`https://www.google.com/maps/search/?api=1&query=${poi.lat},${poi.lng}`} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-amber-500/5 rounded-md hover:bg-amber-500/10 transition-colors"
                onClick={(e) => { if (!isAuthenticated) { e.preventDefault(); handleUnauthorizedAccess(navigate, { returnTo: `/passeio/${tripId}`, message: t("trip_detail.auth_required.maps") }); window.scrollTo(0, 0); } }}>
                <MapPin className="h-2.5 w-2.5 text-amber-500" />
                <span className="text-foreground"><span className="md:hidden">{poi.name.length > 20 ? poi.name.slice(0, 20) + "..." : poi.name}</span><span className="hidden md:inline">{poi.name}</span></span>
              </a>
            ) : (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-muted rounded-md">
                <MapPin className="h-2.5 w-2.5 text-muted-foreground" />
                <span><span className="md:hidden">{poi.name.length > 20 ? poi.name.slice(0, 20) + "..." : poi.name}</span><span className="hidden md:inline">{poi.name}</span></span>
              </span>
            )}
          </span>
        </TooltipTrigger>
        <TooltipContent>
          <p>{t(`trip_form.pois_card.categories.${poi.category}`, poi.category)}</p>
          {poi.description && <p className="text-xs opacity-80">{poi.description}</p>}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );

  return (
    <div>
      <div className="flex items-center gap-1.5 mb-2">
        <MapPin className="h-3 w-3 text-amber-500" />
        <span className="text-xs font-semibold text-foreground uppercase tracking-wider">{t("trip_detail.pois_label", "Pontos de Interesse")}</span>
      </div>
      <div className="flex flex-wrap gap-1">
        {visible.map((poi, idx) => renderPoi(poi, idx))}
        {needsCollapse && !expanded && (
          <button type="button" onClick={() => setExpanded(true)}
            className="inline-flex items-center gap-1 text-[11px] text-amber-600 hover:text-amber-500 transition-colors px-1.5 py-0.5 bg-amber-500/5 rounded-md hover:bg-amber-500/10"
            data-testid="expand-pois-btn">
            <ChevronDown className="h-2.5 w-2.5" />
            {t("trip_detail.show_all_pois", "ver todos")} ({pois.length})
          </button>
        )}
        {needsCollapse && expanded && (
          <button type="button" onClick={() => setExpanded(false)}
            className="inline-flex items-center gap-1 text-[11px] text-amber-600 hover:text-amber-500 transition-colors px-1.5 py-0.5 bg-amber-500/5 rounded-md hover:bg-amber-500/10"
            data-testid="collapse-pois-btn">
            <ChevronUp className="h-2.5 w-2.5" />
            {t("trip_detail.hide_full_route", "ocultar")}
          </button>
        )}
      </div>
    </div>
  );
}

export function SegmentCard({
  segment, index, trip, currentLang, t,
  isAuthenticated, navigate, tripId,
  formatDate, formatDuration,
  highlightedPoint, setHighlightedPoint,
  openAlojamentoLightbox, openFoodLightbox,
  tripExportEnabled = true,
}) {
  // Read the segment's elevation track from the persistent DB record.
  // The backend computes this asynchronously via a throttled background
  // worker at trip-create / trip-update time, so this fetch is a pure
  // DB read with no Open-Meteo dependency on the page-load path.
  //
  // If the data isn't there yet (legacy trips, freshly-edited segments
  // still in the worker queue), the endpoint fires a background job and
  // returns `pending: true`. We then poll a few times so the chart pops
  // in once the worker finishes — no manual refresh needed.
  const [elevationCoords, setElevationCoords] = useState(null);
  const hasRouteSource = !!(segment.gpx_file || segment.google_maps_url);
  useEffect(() => {
    if (!hasRouteSource || !tripId) return;
    let alive = true;
    let cancelTimer = null;

    const fetchOnce = async () => {
      try {
        const res = await fetch(`${API}/api/trips/${tripId}/segments/${index}/elevation`);
        if (!res.ok) return { ok: false };
        const data = await res.json();
        if (!alive) return { ok: false };
        if (data.has_altitude && Array.isArray(data.coordinates) && data.coordinates.length) {
          setElevationCoords(data.coordinates);
          return { ok: true, done: true };
        }
        // b99.9.95 (2026-05-19) — Respect new `status` field. Stop
        // polling for `failed` / `no_source` so we don't loop forever.
        const status = data.status || (data.pending ? "pending" : "no_source");
        return { ok: true, done: status !== "pending", pending: status === "pending" };
      } catch (_e) {
        return { ok: false };
      }
    };

    (async () => {
      // First attempt — most trips return cached data immediately.
      const first = await fetchOnce();
      if (!first.ok || first.done || !first.pending) return;
      // Poll up to 4 more times at increasing intervals while the
      // background worker churns. Caps at ~30s total wait.
      const delays = [3000, 5000, 8000, 12000];
      for (const ms of delays) {
        if (!alive) return;
        await new Promise((resolve) => {
          cancelTimer = setTimeout(resolve, ms);
        });
        if (!alive) return;
        const r = await fetchOnce();
        if (r.done) return;
      }
    })();

    return () => {
      alive = false;
      if (cancelTimer) clearTimeout(cancelTimer);
    };
  }, [tripId, index, hasRouteSource]);

  return (
    <Card
      className="bg-card/80 backdrop-blur-sm border-border rounded-xl overflow-hidden border-t-2 border-t-primary border-b-2 border-b-primary"
      data-testid={`segment-${index}`}
    >
      {/* Segment Header */}
      <div className="bg-gradient-to-r from-primary/25 via-primary/15 to-transparent px-3 md:px-4 py-3">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-2">
          <div className="flex items-center gap-2 md:gap-3">
            <div className="p-2 rounded-lg bg-primary/20 border border-primary/30 flex-shrink-0">
              <Route className="h-3.5 w-3.5 md:h-4 md:w-4 text-primary" />
            </div>
            <div>
              <h3 className="text-sm md:text-base font-semibold text-foreground">
                {getTranslatedValue(segment.title, currentLang)}
              </h3>
              {segment.date && (
                <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                  <Calendar className="h-3 w-3" />
                  {formatDate(segment.date)}
                </p>
              )}
            </div>
          </div>
          {/* Weather badge */}
          {segment.tempo?.condicao && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span
                    className={`flex items-center gap-1.5 md:gap-2 px-2.5 py-1 md:px-4 md:py-2 rounded-xl text-xs md:text-base font-semibold shadow-sm cursor-default self-start ml-9 md:ml-0 ${
                      segment.tempo.condicao === "sol" ? "bg-yellow-500/20 text-yellow-600 border border-yellow-500/30" :
                      segment.tempo.condicao === "nublado_parcial" ? "bg-sky-500/20 text-sky-600 border border-sky-500/30" :
                      segment.tempo.condicao === "nublado" ? "bg-gray-500/20 text-gray-600 border border-gray-500/30" :
                      segment.tempo.condicao === "chuva" ? "bg-blue-500/20 text-blue-600 border border-blue-500/30" :
                      "bg-cyan-500/20 text-cyan-600 border border-cyan-500/30"
                    }`}
                  >
                    {segment.tempo.condicao === "sol" && <Sun className="h-4 w-4 md:h-5 md:w-5" />}
                    {segment.tempo.condicao === "nublado_parcial" && <CloudSun className="h-4 w-4 md:h-5 md:w-5" />}
                    {segment.tempo.condicao === "nublado" && <Cloud className="h-4 w-4 md:h-5 md:w-5" />}
                    {segment.tempo.condicao === "chuva" && <CloudRain className="h-4 w-4 md:h-5 md:w-5" />}
                    {segment.tempo.condicao === "neve" && <Snowflake className="h-4 w-4 md:h-5 md:w-5" />}
                    <span>
                      {segment.tempo.condicao === "sol" && t("trip_detail.weather_sunny")}
                      {segment.tempo.condicao === "nublado_parcial" && t("trip_detail.weather_partly_cloudy")}
                      {segment.tempo.condicao === "nublado" && t("trip_detail.weather_cloudy")}
                      {segment.tempo.condicao === "chuva" && t("trip_detail.weather_rainy")}
                      {segment.tempo.condicao === "neve" && t("trip_detail.weather_snowy")}
                    </span>
                  </span>
                </TooltipTrigger>
                <TooltipContent><p>{t("trip_detail.weather_conditions")}</p></TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
      </div>

      <CardContent className="pt-3 md:pt-4 space-y-3 md:space-y-4 px-3 md:px-6">
        {/* Segment Notes — hidden on single-segment trips: the trip-level
            description already conveys the narrative, so showing the same
            (or near-duplicate) text twice on the public sheet is noise. */}
        {(trip?.segments?.length ?? 0) > 1 && segment.notas && getTranslatedValue(segment.notas, currentLang) && (
          <>
            <div className="p-3 bg-muted/50 rounded-lg border border-border/50" data-testid={`segment-notes-${index}`}>
              <p className="text-xs md:text-sm text-foreground whitespace-pre-line italic">
                {getTranslatedValue(segment.notas, currentLang)}
              </p>
            </div>
            <div className="border-t border-border/50" />
          </>
        )}

        {/* Stats + Locality Sequence — 2-column layout (b99.9.99).
            Each segment exposes its metrics (mini-cards, same look as
            the trip-level stats card in TripDetail.jsx) side-by-side
            with the locality sequence list. When there are no
            localities to show, the stats grid expands to fill the
            available width. */}
        {(() => {
          const cardCls = "flex items-center gap-2 p-3 bg-card border border-border rounded-xl";
          const typeStyle = {
            nacionais: { Icon: Navigation, cls: "text-blue-500", label: t("trip_detail.segment_type_nacionais") },
            touring: { Icon: Globe, cls: "text-orange-500", label: t("trip_detail.segment_type_touring") },
            offroad: { Icon: Mountain, cls: "text-red-500", label: t("trip_detail.segment_type_offroad") },
            estrada: { Icon: Route, cls: "text-primary", label: t("trip_detail.segment_type_road") },
            aventura: { Icon: Mountain, cls: "text-amber-500", label: t("trip_detail.segment_type_adventure") },
          };
          const diffStyle = {
            green: { bar: "bg-green-500", text: "text-green-500", label: t("trip_detail.difficulty_easy_short"), order: 0 },
            blue: { bar: "bg-blue-500", text: "text-blue-500", label: t("trip_detail.difficulty_moderate_short"), order: 1 },
            red: { bar: "bg-yellow-500", text: "text-yellow-500", label: t("trip_detail.difficulty_hard_short"), order: 2 },
            black: { bar: "bg-red-500", text: "text-red-500", label: t("trip_detail.difficulty_expert_short"), order: 3 },
          };
          const tStyle = segment.segment_type ? typeStyle[segment.segment_type] : null;
          const dStyle = segment.difficulty ? diffStyle[segment.difficulty] : null;
          const statsGrid = (
            <div className="grid grid-cols-2 gap-3 md:gap-4 self-start" data-testid={`segment-stats-${index}`}>
              {/* Distance */}
              <div className={cardCls}>
                <Gauge className="h-5 w-5 text-primary shrink-0" />
                <div className="min-w-0">
                  <p className="text-[10px] md:text-xs text-muted-foreground uppercase">{t("trip_detail.distance")}</p>
                  <p className="text-sm font-bold font-mono">{parseFloat((segment.distance_km || 0).toFixed(1))} km</p>
                </div>
              </div>
              {/* Duration */}
              <div className={cardCls}>
                <Clock className="h-5 w-5 text-primary shrink-0" />
                <div className="min-w-0">
                  <p className="text-[10px] md:text-xs text-muted-foreground uppercase">{t("trip_detail.duration")}</p>
                  <p className="text-sm font-bold font-mono">{formatDuration(segment.duration_hours || 0)}</p>
                </div>
              </div>
              {/* Type */}
              {tStyle && (
                <div className={cardCls}>
                  <tStyle.Icon className={`h-5 w-5 shrink-0 ${tStyle.cls}`} />
                  <div className="min-w-0">
                    <p className="text-[10px] md:text-xs text-muted-foreground uppercase">{t("trip_detail.type")}</p>
                    <p className={`text-sm font-bold ${tStyle.cls}`}>{tStyle.label}</p>
                  </div>
                </div>
              )}
              {/* Difficulty */}
              {dStyle && (
                <div className={cardCls}>
                  <div className="flex gap-0.5 shrink-0">
                    {[0, 1, 2, 3].map((i) => (
                      <div key={i} className={`w-1.5 h-5 rounded-sm ${i <= dStyle.order ? dStyle.bar : "bg-muted-foreground/30"}`} />
                    ))}
                  </div>
                  <div className="min-w-0">
                    <p className="text-[10px] md:text-xs text-muted-foreground uppercase">{t("trip_detail.difficulty")}</p>
                    <p className={`text-sm font-bold ${dStyle.text}`}>{dStyle.label}</p>
                  </div>
                </div>
              )}
            </div>
          );

          // Build the localities array using the same precedence rule
          // the previous standalone block used.
          const localitiesArr =
            segment.route_sequence?.length > 0
              ? [...segment.route_sequence].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
              : (segment.localities || []);
          const hasLocs = localitiesArr.length > 0;

          if (!hasLocs) return statsGrid;
          return (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 items-start">
              {statsGrid}
              <LocalitiesRoute
                localities={localitiesArr}
                isAuthenticated={isAuthenticated}
                navigate={navigate}
                tripId={tripId}
                setHighlightedPoint={setHighlightedPoint}
                t={t}
              />
            </div>
          );
        })()}

        {/* POIs list */}
        {segment.pois?.length > 0 && (
          <PoisList
            pois={segment.pois}
            isAuthenticated={isAuthenticated}
            navigate={navigate}
            tripId={tripId}
            setHighlightedPoint={setHighlightedPoint}
            t={t}
          />
        )}

        {/* Links por etapa removidos (GPX e Google Maps): a exportação é
            centralizada — GPX único do passeio na barra por baixo do Mapa
            Total, e a exportação Google Maps vive na ficha da ROTA. A ficha
            pública do passeio não mostra links de rota por etapa. */}

        {/* No routes message */}
        {!segment.google_maps_url && !segment.gpx_file &&
         (!segment.localities || !segment.localities.some((l) => l.lat && l.lng)) &&
         (!segment.pois || !segment.pois.some((p) => p.lat != null && p.lng != null)) && (
          <p className="text-sm text-muted-foreground/50 italic">{t("trip_detail.no_routes")}</p>
        )}

        {/* Estada (Accommodation) */}
        {segment.alojamento?.length > 0 && (
          <div className="pt-4 border-t border-border">
            <div className="space-y-3">
              {segment.alojamento.map((aloj, alojIdx) => (
                <div
                  key={aloj.id || alojIdx}
                  className="p-4 sm:p-5 bg-gradient-to-br from-amber-500/5 to-amber-500/10 rounded-xl border-2 border-amber-500/30"
                  data-testid={`alojamento-card-${alojIdx}`}
                >
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 bg-amber-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Bed className="h-4 w-4 text-amber-600" />
                    </div>
                    <h4 className="font-semibold text-sm md:text-base text-foreground flex-1">
                      {typeof aloj.nome === "object" ? getTranslatedValue(aloj.nome, currentLang) : aloj.nome}
                    </h4>
                    {aloj.link && (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              onClick={() => {
                                if (!isAuthenticated) { handleUnauthorizedAccess(navigate, { returnTo: `/passeio/${tripId}`, message: t("trip_detail.auth_required.accommodation") }); window.scrollTo(0, 0); return; }
                                window.open(aloj.link, "_blank", "noopener,noreferrer");
                              }}
                              className="p-1.5 rounded-lg hover:bg-amber-500/20 transition-colors"
                              data-testid={`alojamento-website-link-${alojIdx}`}
                            >
                              <ExternalLink className="h-4 w-4 text-amber-600 hover:text-amber-500" />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent><p>{t("trip_detail.accommodation_tooltip")}</p></TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                  </div>
                  <div className="flex gap-4 items-start">
                    {(aloj.precos > 0 || aloj.qualidade > 0 || aloj.atendimento > 0) && (
                      <div className="grid grid-cols-[max-content_max-content] gap-x-3 gap-y-1.5 items-center">
                        {aloj.precos > 0 && (
                          <>
                            <span className="text-sm text-muted-foreground">{t("trip_detail.price")}:</span>
                            <div className="flex">{[1,2,3,4,5].map((i) => <Star key={i} className={`h-4 w-4 ${i <= aloj.precos ? "text-amber-500 fill-amber-500" : "text-muted-foreground/30"}`} />)}</div>
                          </>
                        )}
                        {aloj.qualidade > 0 && (
                          <>
                            <span className="text-sm text-muted-foreground">{t("trip_detail.quality")}:</span>
                            <div className="flex">{[1,2,3,4,5].map((i) => <Star key={i} className={`h-4 w-4 ${i <= aloj.qualidade ? "text-amber-500 fill-amber-500" : "text-muted-foreground/30"}`} />)}</div>
                          </>
                        )}
                        {aloj.atendimento > 0 && (
                          <>
                            <span className="text-sm text-muted-foreground">{t("trip_detail.service")}:</span>
                            <div className="flex">{[1,2,3,4,5].map((i) => <Star key={i} className={`h-4 w-4 ${i <= aloj.atendimento ? "text-amber-500 fill-amber-500" : "text-muted-foreground/30"}`} />)}</div>
                          </>
                        )}
                      </div>
                    )}
                    {aloj.notas && (() => { const n = typeof aloj.notas === "object" ? getTranslatedValue(aloj.notas, currentLang) : aloj.notas; return n ? (
                      <div className="flex-1 min-w-0 pr-1">
                        <p className="text-sm text-muted-foreground italic break-words">&ldquo;{n}&rdquo;</p>
                      </div>
                    ) : null; })()}
                  </div>
                  {aloj.photos?.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-amber-500/20">
                      <div className="flex gap-2 overflow-x-auto pb-1">
                        {aloj.photos.map((photo, photoIdx) => (
                          <div key={photo.photo_id || photoIdx} className="relative flex-shrink-0 cursor-pointer"
                            onClick={() => openAlojamentoLightbox(aloj.photos, photoIdx, typeof aloj.nome === "object" ? getTranslatedValue(aloj.nome, currentLang) : aloj.nome)}
                            data-testid={`alojamento-photo-${alojIdx}-${photoIdx}`}
                          >
                            <img src={getCloudinaryThumbnail(getUploadUrl(photo.filename), 200, 150)} alt={typeof photo.caption === 'object' ? (getTranslatedValue(photo.caption, currentLang) || `Foto ${photoIdx + 1}`) : (photo.caption || `Foto ${photoIdx + 1}`)} className="h-16 w-24 object-cover rounded-lg border border-amber-500/30 hover:border-amber-500 transition-colors" loading="lazy" />
                            {photo.caption && (() => { const c = typeof photo.caption === 'object' ? getTranslatedValue(photo.caption, currentLang) : photo.caption; return c ? <p className="text-[9px] text-muted-foreground mt-0.5 w-24 truncate" title={c}>{c}</p> : null; })()}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Comes e Bebes */}
        {segment.restauracao?.length > 0 && (
          <div className={`${segment.alojamento?.length > 0 ? "" : "pt-4 border-t border-border"}`}>
            <div className="space-y-3">
              {segment.restauracao.map((item, cbIdx) => (
                <div
                  key={item.id || cbIdx}
                  className="p-4 sm:p-5 bg-gradient-to-br from-orange-500/5 to-orange-500/10 rounded-xl border-2 border-orange-500/30"
                  data-testid={`food-card-${cbIdx}`}
                >
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 bg-orange-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <UtensilsCrossed className="h-4 w-4 text-orange-600" />
                    </div>
                    <h4 className="font-semibold text-sm md:text-base text-foreground flex-1">
                      {typeof item.nome === "object" ? getTranslatedValue(item.nome, currentLang) : item.nome}
                    </h4>
                    {item.link && (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              onClick={() => {
                                if (!isAuthenticated) { handleUnauthorizedAccess(navigate, { returnTo: `/passeio/${tripId}`, message: t("trip_detail.auth_required.restaurant") }); window.scrollTo(0, 0); return; }
                                window.open(item.link, "_blank", "noopener,noreferrer");
                              }}
                              className="p-1.5 rounded-lg hover:bg-orange-500/20 transition-colors"
                              data-testid={`food-website-link-${cbIdx}`}
                            >
                              <ExternalLink className="h-4 w-4 text-orange-600 hover:text-orange-500" />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent><p>{t("trip_detail.food_tooltip")}</p></TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                  </div>
                  <div className="flex gap-4 items-start">
                    {(item.custo > 0 || item.qualidade > 0 || item.atendimento > 0) && (
                      <div className="grid grid-cols-[max-content_max-content] gap-x-3 gap-y-1.5 items-center">
                        {item.custo > 0 && (
                          <>
                            <span className="text-sm text-muted-foreground">{t("trip_detail.price")}:</span>
                            <div className="flex">{[1,2,3,4,5].map((i) => <Star key={i} className={`h-4 w-4 ${i <= item.custo ? "text-orange-500 fill-orange-500" : "text-muted-foreground/30"}`} />)}</div>
                          </>
                        )}
                        {item.qualidade > 0 && (
                          <>
                            <span className="text-sm text-muted-foreground">{t("trip_detail.quality")}:</span>
                            <div className="flex">{[1,2,3,4,5].map((i) => <Star key={i} className={`h-4 w-4 ${i <= item.qualidade ? "text-orange-500 fill-orange-500" : "text-muted-foreground/30"}`} />)}</div>
                          </>
                        )}
                        {item.atendimento > 0 && (
                          <>
                            <span className="text-sm text-muted-foreground">{t("trip_detail.service")}:</span>
                            <div className="flex">{[1,2,3,4,5].map((i) => <Star key={i} className={`h-4 w-4 ${i <= item.atendimento ? "text-orange-500 fill-orange-500" : "text-muted-foreground/30"}`} />)}</div>
                          </>
                        )}
                      </div>
                    )}
                    {item.notas && (() => { const n = typeof item.notas === "object" ? getTranslatedValue(item.notas, currentLang) : item.notas; return n ? (
                      <div className="flex-1 min-w-0 pr-1">
                        <p className="text-sm text-muted-foreground italic break-words">&ldquo;{n}&rdquo;</p>
                      </div>
                    ) : null; })()}
                  </div>
                  {item.photos?.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-orange-500/20">
                      <div className="flex gap-2 overflow-x-auto pb-1">
                        {item.photos.map((photo, photoIdx) => (
                          <div key={photo.photo_id || photoIdx} className="relative flex-shrink-0 cursor-pointer"
                            onClick={() => openFoodLightbox(item.photos, photoIdx, typeof item.nome === "object" ? getTranslatedValue(item.nome, currentLang) : item.nome)}
                            data-testid={`food-photo-${cbIdx}-${photoIdx}`}
                          >
                            <img src={getCloudinaryThumbnail(getUploadUrl(photo.filename), 200, 150)} alt={typeof photo.caption === 'object' ? (getTranslatedValue(photo.caption, currentLang) || `Foto ${photoIdx + 1}`) : (photo.caption || `Foto ${photoIdx + 1}`)} className="h-16 w-24 object-cover rounded-lg border border-orange-500/30 hover:border-orange-500 transition-colors" loading="lazy" />
                            {photo.caption && (() => { const c = typeof photo.caption === 'object' ? getTranslatedValue(photo.caption, currentLang) : photo.caption; return c ? <p className="text-[9px] text-muted-foreground mt-0.5 w-24 truncate" title={c}>{c}</p> : null; })()}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
